import { UserOutlined, SearchOutlined } from '@ant-design/icons';
import { Avatar, Form, Input, Listy, Modal, Spin } from 'antd';
import { TFunction } from 'i18next';
import { IDocument, membersType } from 'interfaces';
import React from 'react';
import { useNavigate } from 'react-router';
import { useAppDispatch, useAppSelector } from 'store/hook';
import { createGLobalChat, createChat, fetchSetSelectedChat } from 'store/reducers/chatCreator';
import { setSelectedKeys, setIsUploadingDocuments } from 'store/slices/windowStateSlice';
import classes from './CreateChatModal.module.scss';
import { availableIconFormat, documnentIcons } from '../../../helpers/documentIcons';
import cn from 'classnames';
import { getTheme } from 'helpers/getTheme';

type Props = {
  title: string;
  open: boolean;
  ownerId: string;
  members: membersType[];
  onCancel: () => void;
  onClickItem: (member: membersType) => void;
  t: TFunction;
  currentSegment: 'main' | 'room';
  documents: IDocument[];
  isLoadingDocuments: boolean;
  };

export default function CreateChatModal({ title, open, onCancel, members, documents, isLoadingDocuments,  onClickItem, ownerId, t, currentSegment }: Props) {  
  const [checkedMembers, setCheckedMembers] = React.useState<string[]>([]);
  const [searchValue, setSearchValue] = React.useState<string>('');
  const { dataRoom } = useAppSelector((state) => state.dataRoom);
  const { userData } = useAppSelector((state) => state.userData);
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { roomAllChats } = useAppSelector((state) => state.chat);
  const modalStatusFromSegment: 'privateCreate' | 'documentCreate' =
    currentSegment === 'main' ? 'privateCreate' : 'documentCreate';

  const onCreateUserChat = async (values: { title: string }) => {
    const oponentName = members.find(({ id }) => checkedMembers[0] === id)?.name;
    onCancel();
    const chatInfo = await dispatch(
      createGLobalChat({
        data: {
          entity_type: 2,
          room_id: dataRoom?.id!,
          members_ids: [ownerId, ...(checkedMembers as string[])],
          title: values.title || '',
        },
        ownerId: ownerId,
        oponentName,
      })
    );
    navigate(`/room/${dataRoom?.custom_url || dataRoom?.id}/chat/user/${(chatInfo.payload as any).chat.id}`)
  };

  const onCreateDocumentChat = async (document: IDocument) => {
    if(document?.status === 0) return;
    onCancel();
    dispatch(setIsUploadingDocuments(true));
    const chatInfo = await dispatch(
      createChat({
        data: {
          entity_type: 0,
          entity_id: document.id,
          members_ids: dataRoom?.available_users?.map(({ id }) => id)!,
          title: document.name,
          room_id: dataRoom?.id!,
        },
        ownerId: userData?.id!,
      })
    );
    dispatch(setSelectedKeys(['chat']));
    dispatch(setIsUploadingDocuments(false));
    dispatch(fetchSetSelectedChat((chatInfo.payload as any).chat));
    navigate(`/room/${dataRoom?.custom_url || dataRoom?.id}/chat/document/${(chatInfo.payload as any).chat.id}`);
  };
 

  const filteredDocuments: IDocument[] = React.useMemo(() => {
    const activeDocChatEntityIds = new Set(roomAllChats
      .filter((chat) => chat.entity_type === 0 && !chat.deleted_at)
      .map((chat) => chat.entity_id));

    return (documents).filter((doc) => {
      const isPending = doc.status === 0;
      const hasChat = activeDocChatEntityIds.has(doc.id);
      const matchesSearch = doc.name.toLowerCase().includes(searchValue.toLowerCase());
 
      return !isPending && !hasChat && matchesSearch;
    });
  }, [documents, roomAllChats, searchValue]);

  const modalCases = {
    privateCreate: (
      <Listy
        items={members}
        rowKey='id'
        className={classes.list}
        itemRender={(item) => (
          <div
            key={item.id}
            className={classes.list_item}
            style={{ cursor: ownerId !== item.id ? 'pointer' : 'default' }}
            onClick={() => ownerId !== item.id && onClickItem(item)}
          >
            <div className={classes.list_meta} style={{ paddingLeft: '10px' }}>
              <Avatar
                className={classes.avatar}
                icon={<UserOutlined />}
              />
              <div className={classes.text_block}>
                <div>{item.name}</div>
                <div className={classes.email}>{item.email}</div>
              </div>
            </div>
          </div>
        )}
      />
    ),
    documentCreate: (
      <>
        <Input
          prefix={<SearchOutlined />}
          placeholder={t('Chats.modals.create.searchDocuments')}
          value={searchValue}
          onChange={(e) => setSearchValue(e.target.value)}
          style={{ marginBottom: 16 }}
          allowClear
        />
        <Spin style={{ paddingBottom: isLoadingDocuments ? 40 : 0}} spinning={isLoadingDocuments}>
          <Listy
            items={filteredDocuments}
            rowKey='id'
            className={classes.list}
            itemRender={(item) => {
              const fileType = item.extension?.toLowerCase();
              const iconKey = availableIconFormat.includes(fileType as string)
                ? (fileType as string)
                : 'default';

              return (
                <div
                  key={item.id}
                  className={classes.list_item}
                  style={{ cursor: 'pointer' }}
                  onClick={() => onCreateDocumentChat(item)}
                >
                  <div className={classes.list_meta} style={{ paddingLeft: '10px' }}>
                    <Avatar
                      size={40}
                      className={classes.icon}
                      icon={documnentIcons[iconKey as keyof typeof documnentIcons]}
                    />

                    <div className={classes.text_block}>
                      <div>{item.name}</div>
                    </div>
                  </div>
                </div>
              );
            }}
          />
        </Spin>
      </>
    ),
  };

  return (
    <Modal
      title={title}
      open={open}
      onCancel={onCancel}
      footer={null}
      centered
      className={cn(classes.modal, getTheme())}
      width={550}
    >
      <Form onFinish={onCreateUserChat}>
        <div className={classes.modal_body}>
          {checkedMembers.length > 1 && (
            <Form.Item name='title'>
              <Input placeholder='Type title of new chat' />
            </Form.Item>
          )}
          {modalCases[modalStatusFromSegment]}
        </div>
      </Form>
    </Modal>
  );
}
